import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Card, CardContent, CardHeader, CardTitle,
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '../components/ui/table';
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer,
  ReferenceLine, PieChart, Pie, Cell, BarChart, Bar, Legend,
} from 'recharts';
import EmptyState from '../components/EmptyState';
import { AssetPicker } from '../components/AssetPicker';
import PortfolioAIActions from '../components/PortfolioAIActions';
import {
  getPortfolio, updatePortfolio, deletePortfolio, getPortfolioStats,
  listPortfolioTrades, openPortfolioOrder, closePortfolioOrder,
  addPastOperation, quantPriceAt, quantPriceLatest,
  listLinkedStrategies, portfolioHistory,
  formatCurrency, formatDate,
} from '../lib/api';
import {
  ArrowLeft, Trash2, Plus, TrendingUp, TrendingDown,
  Briefcase, Sparkles, History, X, Loader2, PieChart as PieIcon,
  Info, FlaskConical,
} from 'lucide-react';
import {
  Tooltip as UITooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '../components/ui/tooltip';

const TYPE_BADGE = {
  synthetic_from_scratch: { label: 'Sintetico', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' },
  synthetic_live: { label: 'Sintetico live', color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300' },
  real_reconstructed: { label: 'Reale ricostruito', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' },
};

const PortfolioDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [portfolio, setPortfolio] = useState(null);
  const [stats, setStats] = useState(null);
  const [trades, setTrades] = useState([]);
  const [linkedStrategies, setLinkedStrategies] = useState([]);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Dialogs
  const [openTradeDialog, setOpenTradeDialog] = useState(false);
  const [closeTradeDialog, setCloseTradeDialog] = useState(null);  // trade obj or null
  const [pastOpDialog, setPastOpDialog] = useState(false);
  const [addAssetDialog, setAddAssetDialog] = useState(false);
  const [newAssetSymbol, setNewAssetSymbol] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Auto-fetch prezzo storico nel form Operazione passata (S6.A.3)
  const [pPriceLoading, setPPriceLoading] = useState(false);
  const [pPriceSuggested, setPPriceSuggested] = useState(null);

  // Mark-to-market: prezzi correnti per i trade open (S6.A.4)
  const [latestPrices, setLatestPrices] = useState({}); // {symbol: close}

  // Open trade form
  const [oSymbol, setOSymbol] = useState('');
  const [oQty, setOQty] = useState('');
  const [oPrice, setOPrice] = useState('');
  const [oStrategy, setOStrategy] = useState('');

  // Close trade form
  const [cPrice, setCPrice] = useState('');

  // Past operation form
  const [pDate, setPDate] = useState(new Date().toISOString().slice(0, 10));
  const [pSide, setPSide] = useState('buy');
  const [pSymbol, setPSymbol] = useState('');
  const [pQty, setPQty] = useState('');
  const [pPrice, setPPrice] = useState('');
  const [pAddedCount, setPAddedCount] = useState(0);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [pRes, sRes, tRes, strategiesRes] = await Promise.all([
        getPortfolio(id),
        getPortfolioStats(id),
        listPortfolioTrades(id, { limit: 500 }),
        listLinkedStrategies(id).catch(() => ({ data: { items: [] } })),
      ]);
      setPortfolio(pRes.data);
      setStats(sRes.data);
      setTrades(tRes.data.items || []);
      setLinkedStrategies(strategiesRes.data.items || []);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 404) {
        toast.error('Portafoglio non trovato');
        navigate('/portfolios');
      } else {
        toast.error('Errore nel caricamento');
      }
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Equity curve storica: fetch separato (può essere lento per portfolio
  // grandi — ~250 trading days × N ticker). Non blocca il primo render.
  useEffect(() => {
    if (!trades.length) return;
    let cancelled = false;
    setHistoryLoading(true);
    portfolioHistory(id)
      .then((r) => { if (!cancelled) setHistory(r.data.items || []); })
      .catch(() => { if (!cancelled) setHistory([]); })
      .finally(() => { if (!cancelled) setHistoryLoading(false); });
    return () => { cancelled = true; };
  }, [id, trades.length]);

  // Mark-to-market: fetch prezzo latest per ogni symbol con trade open (S6.A.4)
  useEffect(() => {
    const openSymbols = [...new Set(
      trades.filter((t) => t.status === 'open').map((t) => t.symbol)
    )];
    if (!openSymbols.length) return;
    let cancelled = false;
    Promise.all(
      openSymbols.map((s) =>
        quantPriceLatest(s)
          .then((r) => [s, r.data.close_eur ?? r.data.close])
          .catch(() => [s, null])
      )
    ).then((entries) => {
      if (cancelled) return;
      const next = {};
      for (const [s, p] of entries) {
        if (p !== null) next[s] = p;
      }
      setLatestPrices(next);
    });
    return () => { cancelled = true; };
  }, [trades]);

  // Auto-fetch prezzo storico quando l'utente compila data + symbol nel form
  // operazione passata (S6.A.3). Debounce 400ms.
  useEffect(() => {
    if (!pastOpDialog || !pSymbol || !pDate) {
      setPPriceSuggested(null);
      return;
    }
    let cancelled = false;
    setPPriceLoading(true);
    const t = setTimeout(() => {
      quantPriceAt(pSymbol, pDate)
        .then((r) => {
          if (cancelled) return;
          setPPriceSuggested(r.data);
          // Pre-popola con il prezzo in EUR (se ticker USD, è già convertito)
          const priceEur = r.data.close_eur ?? r.data.close;
          setPPrice((curr) => curr || priceEur.toFixed(2));
        })
        .catch(() => {
          if (!cancelled) setPPriceSuggested({ error: true });
        })
        .finally(() => { if (!cancelled) setPPriceLoading(false); });
    }, 400);
    return () => { cancelled = true; clearTimeout(t); };
  }, [pSymbol, pDate, pastOpDialog]);

  // Aggregati Fineco-style su open positions (S6.A.6)
  const portfolioBreakdown = useMemo(() => {
    let costBasis = 0;
    let marketValue = 0;
    let feesPaidEntry = 0;
    let unrealizedKnownMV = 0; // mark-to-market solo per i trade con current price disponibile
    let priceMissing = 0;
    for (const t of trades) {
      if (t.status !== 'open') continue;
      const qty = t.qty || 0;
      const entry = t.entry?.price || 0;
      const fees = t.fees_entry || 0;
      costBasis += qty * entry;
      feesPaidEntry += fees;
      const current = latestPrices[t.symbol];
      if (current != null) {
        marketValue += qty * current;
        unrealizedKnownMV += qty * current;
      } else {
        priceMissing++;
        // Se manca il current price, fallback al costo (non penalizziamo il mark-to-market)
        marketValue += qty * entry;
      }
    }
    return { costBasis, marketValue, feesPaidEntry, priceMissing, unrealizedKnownMV };
  }, [trades, latestPrices]);

  // PnL unrealized = market value - cost basis - fees pagate sull'entry
  const unrealizedPnL = portfolioBreakdown.marketValue - portfolioBreakdown.costBasis - portfolioBreakdown.feesPaidEntry;

  // Aggregati per asset per i grafici (allocazione + PnL per simbolo)
  const perAssetBreakdown = useMemo(() => {
    const map = new Map();
    for (const t of trades) {
      if (t.status !== 'open') continue;
      const qty = t.qty || 0;
      const entry = t.entry?.price || 0;
      const fee = t.fees_entry || 0;
      const current = latestPrices[t.symbol] ?? entry;
      const cost = qty * entry;
      const value = qty * current;
      const cur = map.get(t.symbol) || { symbol: t.symbol, qty: 0, cost: 0, value: 0, fees: 0 };
      cur.qty += qty;
      cur.cost += cost;
      cur.value += value;
      cur.fees += fee;
      map.set(t.symbol, cur);
    }
    const rows = Array.from(map.values()).map((r) => ({
      ...r,
      pnl: r.value - r.cost - r.fees,
      pnl_pct: r.cost > 0 ? ((r.value - r.cost - r.fees) / r.cost) * 100 : 0,
    }));
    return rows.sort((a, b) => b.value - a.value);
  }, [trades, latestPrices]);

  // Palette colori coerente per asset
  const ASSET_COLORS = [
    'hsl(150, 60%, 45%)', 'hsl(220, 70%, 55%)', 'hsl(280, 60%, 60%)',
    'hsl(35, 85%, 55%)', 'hsl(0, 70%, 55%)', 'hsl(195, 75%, 50%)',
    'hsl(330, 65%, 55%)',
  ];

  // Pie chart: allocazione patrimonio (asset + liquidità)
  const allocationData = useMemo(() => {
    const items = perAssetBreakdown.map((r, i) => ({
      name: r.symbol, value: r.value, color: ASSET_COLORS[i % ASSET_COLORS.length],
    }));
    const cash = stats?.liquidity ?? portfolio?.budget_initial ?? 0;
    if (cash > 0) {
      items.push({ name: 'Liquidità', value: cash, color: 'hsl(0, 0%, 60%)' });
    }
    return items;
  }, [perAssetBreakdown, stats, portfolio]);

  // Equity curve dai trade closed (cronologico)
  const equityCurve = useMemo(() => {
    if (!portfolio || !trades.length) return [];
    const closed = trades
      .filter((t) => t.status === 'closed' && t.pnl_abs_net !== null && t.exit?.filled_at)
      .sort((a, b) => new Date(a.exit.filled_at) - new Date(b.exit.filled_at));
    let running = portfolio.budget_initial;
    const start = portfolio.created_at;
    const curve = [{
      ts: start ? new Date(start).getTime() : Date.now(),
      label: start ? formatDate(start) : 'Inizio',
      equity: portfolio.budget_initial,
    }];
    for (const t of closed) {
      running += t.pnl_abs_net;
      curve.push({
        ts: new Date(t.exit.filled_at).getTime(),
        label: formatDate(t.exit.filled_at),
        equity: Math.round(running * 100) / 100,
      });
    }
    return curve;
  }, [portfolio, trades]);

  const updateCoverage = async (newCoverage) => {
    try {
      const r = await updatePortfolio(id, { asset_coverage: newCoverage });
      setPortfolio(r.data);
      toast.success('Asset aggiornati');
    } catch (e) {
      toast.error('Errore aggiornamento asset');
    }
  };

  const handleAddAsset = async () => {
    if (!newAssetSymbol) return;
    const sym = newAssetSymbol.toUpperCase();
    if ((portfolio.asset_coverage || []).includes(sym)) {
      toast.info(`${sym} è già nel portafoglio`);
      return;
    }
    await updateCoverage([...(portfolio.asset_coverage || []), sym]);
    setNewAssetSymbol('');
    setAddAssetDialog(false);
  };

  const handleRemoveAsset = async (sym) => {
    const hasTrades = trades.some((t) => t.symbol === sym);
    const msg = hasTrades
      ? `Rimuovere ${sym} dalla copertura?\nI trade esistenti su ${sym} restano in tabella (non cancellati), ma il ticker non comparirà più nei suggerimenti.`
      : `Rimuovere ${sym} dalla copertura?`;
    if (!window.confirm(msg)) return;
    await updateCoverage((portfolio.asset_coverage || []).filter((s) => s !== sym));
  };

  const handleDelete = async () => {
    if (!window.confirm(`Eliminare "${portfolio.name}"?\nTutti i trade saranno persi.`)) return;
    try {
      await deletePortfolio(id);
      toast.success('Portafoglio eliminato');
      navigate('/portfolios');
    } catch (e) {
      toast.error("Errore nell'eliminazione");
    }
  };

  const handleOpenTrade = async (e) => {
    e.preventDefault();
    const qty = parseFloat(oQty);
    const price = parseFloat(oPrice);
    if (!oSymbol.trim() || !(qty > 0) || !(price > 0)) {
      toast.error('Compila simbolo, qty e prezzo');
      return;
    }
    setSubmitting(true);
    try {
      await openPortfolioOrder(id, {
        symbol: oSymbol.trim().toUpperCase(),
        qty,
        entry_price: price,
        strategy: oStrategy.trim() || null,
      });
      toast.success('Trade aperto');
      setOpenTradeDialog(false);
      setOSymbol(''); setOQty(''); setOPrice(''); setOStrategy('');
      await loadAll();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Errore apertura trade');
    } finally {
      setSubmitting(false);
    }
  };

  const openPastOpDialog = () => {
    setPDate(new Date().toISOString().slice(0, 10));
    setPSide('buy');
    setPSymbol('');
    setPQty('');
    setPPrice('');
    setPAddedCount(0);
    setPastOpDialog(true);
  };

  const handleAddPastOp = async (e, addAnother = false) => {
    e.preventDefault();
    const qty = parseFloat(pQty);
    const price = parseFloat(pPrice);
    if (!pSymbol.trim() || !(qty > 0) || !(price > 0) || !pDate) {
      toast.error('Compila tutti i campi');
      return;
    }
    setSubmitting(true);
    try {
      await addPastOperation(id, {
        date: pDate,
        symbol: pSymbol.trim().toUpperCase(),
        qty,
        price,
        side: pSide,
      });
      toast.success(`${pSide === 'buy' ? 'BUY' : 'SELL'} ${qty} ${pSymbol.toUpperCase()} @ ${price} registrato`);
      setPAddedCount((n) => n + 1);
      if (addAnother) {
        // Reset form ma tieni la data per accelerare l'inserimento di operazioni vicine
        setPSymbol('');
        setPQty('');
        setPPrice('');
      } else {
        setPastOpDialog(false);
      }
      await loadAll();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Errore aggiunta operazione');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseTrade = async (e) => {
    e.preventDefault();
    const price = parseFloat(cPrice);
    if (!(price > 0)) {
      toast.error('Inserisci un prezzo di uscita valido');
      return;
    }
    setSubmitting(true);
    try {
      await closePortfolioOrder(id, closeTradeDialog.id, { exit_price: price });
      toast.success('Trade chiuso');
      setCloseTradeDialog(null);
      setCPrice('');
      await loadAll();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Errore chiusura trade');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="text-center py-12 text-muted-foreground">Caricamento…</div>;
  }
  if (!portfolio) return null;

  const badge = TYPE_BADGE[portfolio.type] || TYPE_BADGE.synthetic_from_scratch;
  // Sprint 6.A.7: KPI Fineco-style ora vengono dal backend (compute_stats live
  // mark-to-market in EUR). Mantengo il calcolo client-side come fallback solo
  // se stats è ancora null durante il primo render.
  const cash = stats?.liquidity ?? portfolio.budget_initial;
  const costBasis = stats?.cost_basis_open ?? portfolioBreakdown.costBasis;
  const marketValue = stats?.market_value ?? portfolioBreakdown.marketValue;
  const totalPatrimony = stats?.patrimony_total ?? (cash + marketValue);
  const pnlClosed = stats?.total_pnl_abs_net ?? 0;
  const pnlOpen = stats?.unrealized_pnl ?? unrealizedPnL;
  const pnlTotal = stats?.pnl_total ?? (pnlClosed + pnlOpen);
  const pnlPctOnCost = stats?.pnl_pct_on_cost ?? (costBasis > 0 ? (pnlTotal / costBasis) * 100 : null);
  const pricesMissing = stats?.prices_missing ?? portfolioBreakdown.priceMissing;
  const winRatePct = stats?.win_rate != null ? Math.round(stats.win_rate * 100) : null;
  const isReal = portfolio.type === 'real_reconstructed';
  const feesTotal = stats?.total_fees_paid ?? 0;

  return (
    <TooltipProvider delayDuration={150}>
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="flex items-start gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/portfolios')} aria-label="Indietro">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Briefcase className="w-6 h-6 text-primary" />
              {portfolio.name}
            </h1>
            <div className="flex items-center gap-2 flex-wrap mt-1">
              <span className={`text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded ${badge.color}`}>
                {badge.label}
              </span>
              {portfolio.strategy_name && (
                <Badge variant="outline" className="text-xs">{portfolio.strategy_name}</Badge>
              )}
              {portfolio.asset_coverage?.length > 0 && (
                <span className="text-xs text-muted-foreground">
                  {portfolio.asset_coverage.join(', ')}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isReal ? (
            <Button
              variant="outline"
              onClick={openPastOpDialog}
              data-testid="add-past-trade"
            >
              <History className="w-4 h-4 mr-2" />
              Operazione passata
            </Button>
          ) : (
            <Button onClick={() => setOpenTradeDialog(true)} data-testid="open-trade-btn">
              <Plus className="w-4 h-4 mr-2" />
              Apri trade
            </Button>
          )}
          <Button variant="ghost" size="icon" onClick={handleDelete} aria-label="Elimina portafoglio">
            <Trash2 className="w-4 h-4 text-destructive" />
          </Button>
        </div>
      </div>

      {/* AI Action Toolbar dedicata al portafoglio (Sprint 5) */}
      <div className="bg-muted/30 border rounded-lg p-3 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 text-sm">
          <Sparkles className="w-4 h-4 text-primary" />
          <span className="font-medium">AI sul portafoglio</span>
          <span className="text-xs text-muted-foreground hidden sm:inline">
            Diagnosi onesta · variante strategia · strategia nuova ad-hoc
          </span>
        </div>
        <PortfolioAIActions portfolio={portfolio} onComplete={loadAll} />
      </div>

      {/* Asset coperti — gestione live (S6.A.2) */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            Asset coperti
            <span className="text-xs text-muted-foreground font-normal">
              ({(portfolio.asset_coverage || []).length})
            </span>
          </CardTitle>
          <Button size="sm" onClick={() => setAddAssetDialog(true)} data-testid="add-asset-btn">
            <Plus className="w-4 h-4 mr-1.5" />
            Aggiungi asset
          </Button>
        </CardHeader>
        <CardContent>
          {(portfolio.asset_coverage || []).length === 0 ? (
            <p className="text-sm text-muted-foreground py-2">
              Nessun asset ancora. Aggiungili man mano che fai operazioni nel portafoglio reale.
            </p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {portfolio.asset_coverage.map((sym) => (
                <Badge
                  key={sym}
                  variant="secondary"
                  className="flex items-center gap-1.5 pr-1 py-1 text-sm"
                >
                  <span className="font-mono">{sym}</span>
                  <button
                    type="button"
                    onClick={() => handleRemoveAsset(sym)}
                    className="hover:bg-destructive/20 rounded p-0.5"
                    aria-label={`Rimuovi ${sym}`}
                  >
                    <X className="w-3 h-3" />
                  </button>
                </Badge>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* KPI grid Fineco-style (S6.A.6) */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KPICard
          label="Liquidità"
          value={formatCurrency(cash)}
          subtitle="cash residuo (budget iniziale)"
        />
        <KPICard
          label="Capitale investito"
          value={formatCurrency(costBasis)}
          subtitle={`${trades.filter((t) => t.status === 'open').length} posizioni open`}
        />
        <KPICard
          label="Valorizzazione"
          value={formatCurrency(marketValue)}
          subtitle={pricesMissing > 0
            ? `${pricesMissing} prezzo mancante`
            : 'mark-to-market live (EUR)'}
        />
        <KPICard
          label="Patrimonio totale"
          value={formatCurrency(totalPatrimony)}
          subtitle="liquidità + valorizzazione"
        />
        <UITooltip>
          <TooltipTrigger asChild>
            <div>
              <KPICard
                label={<span className="flex items-center gap-1">PnL netto <Info className="w-3 h-3 opacity-50" /></span>}
                value={formatCurrency(pnlTotal)}
                subtitle={pnlPctOnCost != null
                  ? `${pnlPctOnCost >= 0 ? '+' : ''}${pnlPctOnCost.toFixed(2)}% su capitale investito`
                  : null}
                highlight={pnlTotal > 0 ? 'positive' : pnlTotal < 0 ? 'negative' : 'neutral'}
              />
            </div>
          </TooltipTrigger>
          <TooltipContent className="max-w-sm text-xs">
            <div className="space-y-1">
              <div className="font-medium">PnL netto = mark-to-market − fees pagate</div>
              <div className="text-muted-foreground">
                Fineco mostra il <em>PnL lordo</em> (senza sottrarre le commissioni).
                Il nostro PnL netto è <strong>{formatCurrency(pnlTotal)}</strong>, il lordo
                stimato sarebbe <strong>{formatCurrency(pnlTotal + feesTotal)}</strong>.
              </div>
              <div className="text-muted-foreground pt-1 border-t border-border/30">
                Diff residuo vs Fineco: cambio yfinance EUR/USD (close giorno) vs
                cambio Fineco real-time. Ininfluente per ticker EUR diretti (IWDE.MI, IUSE.MI).
              </div>
            </div>
          </TooltipContent>
        </UITooltip>
      </div>

      {/* Mini-stats secondari */}
      {(stats?.n_trades || 0) > 0 && (
        <div className="text-xs text-muted-foreground flex flex-wrap gap-4">
          <span>Trade: <strong className="text-foreground">{stats.n_trades}</strong> ({stats.n_open || 0} open · {stats.n_closed || 0} chiusi)</span>
          {winRatePct != null && (
            <span>Win rate: <strong className="text-foreground">{winRatePct}%</strong></span>
          )}
          {stats.total_fees_paid > 0 && (
            <span>Fees pagate: <strong className="text-foreground">{formatCurrency(stats.total_fees_paid)}</strong></span>
          )}
          <span>PnL realized (chiusi): <strong className={pnlClosed > 0 ? 'text-emerald-600 dark:text-emerald-400' : pnlClosed < 0 ? 'text-red-600 dark:text-red-400' : 'text-foreground'}>{formatCurrency(pnlClosed)}</strong></span>
          <span>PnL unrealized (open): <strong className={pnlOpen > 0 ? 'text-emerald-600 dark:text-emerald-400' : pnlOpen < 0 ? 'text-red-600 dark:text-red-400' : 'text-foreground'}>{formatCurrency(pnlOpen)}</strong></span>
        </div>
      )}

      {/* Strategie collegate (Sprint 6.A.10) */}
      {linkedStrategies.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <FlaskConical className="w-4 h-4" />
              Strategie collegate
              <span className="text-xs text-muted-foreground font-normal">
                ({linkedStrategies.length})
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {linkedStrategies.map((s) => (
              <div
                key={s.name}
                className="border rounded-md p-3 hover:bg-muted/30 transition-colors"
              >
                <div className="flex items-start justify-between gap-3 mb-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-sm font-medium">{s.name}</span>
                    <Badge variant="outline" className="text-[10px]">
                      v{s.version}
                    </Badge>
                    {s._role === 'declared' && (
                      <Badge variant="secondary" className="text-[10px]">dichiarata</Badge>
                    )}
                    {s._role === 'ai_generated' && (
                      <Badge variant="secondary" className="text-[10px] bg-primary/10 text-primary">
                        AI-generated
                      </Badge>
                    )}
                  </div>
                  {s.previous_version && (
                    <span className="text-xs text-muted-foreground">
                      ← variante di {s.previous_version}
                    </span>
                  )}
                </div>
                {s.description && (
                  <p className="text-xs text-muted-foreground line-clamp-2">
                    {s.description}
                  </p>
                )}
                {s.universe?.length > 0 && (
                  <div className="text-xs text-muted-foreground mt-1.5">
                    Universe: <span className="font-mono">{s.universe.join(', ')}</span>
                  </div>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Composizione portafoglio: pie allocation + bar PnL per asset (Sprint 6.A.8) */}
      {perAssetBreakdown.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <PieIcon className="w-4 h-4" />
                Allocazione patrimonio
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie
                    data={allocationData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={90}
                    innerRadius={48}
                    paddingAngle={2}
                  >
                    {allocationData.map((entry, idx) => (
                      <Cell key={idx} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(v, n) => [formatCurrency(v), n]}
                  />
                  <Legend
                    layout="vertical"
                    align="right"
                    verticalAlign="middle"
                    iconType="circle"
                    formatter={(value, entry) => {
                      const item = allocationData.find((a) => a.name === value);
                      const pct = item && totalPatrimony > 0
                        ? ((item.value / totalPatrimony) * 100).toFixed(1) : '0';
                      return <span className="text-xs">{value} <span className="text-muted-foreground">({pct}%)</span></span>;
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <TrendingUp className="w-4 h-4" />
                PnL per asset
                <span className="text-xs text-muted-foreground font-normal">(unrealized, netto fees)</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={perAssetBreakdown} layout="vertical" margin={{ left: 8, right: 24 }}>
                  <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(v) => `€${v.toFixed(0)}`} />
                  <YAxis dataKey="symbol" type="category" tick={{ fontSize: 11 }} width={70} />
                  <Tooltip
                    formatter={(v, n, p) => {
                      if (n === 'pnl') {
                        const row = p.payload;
                        return [
                          `${formatCurrency(v)} (${row.pnl_pct >= 0 ? '+' : ''}${row.pnl_pct.toFixed(2)}%)`,
                          'PnL netto',
                        ];
                      }
                      return [v, n];
                    }}
                  />
                  <ReferenceLine x={0} stroke="hsl(0, 0%, 50%)" />
                  <Bar dataKey="pnl" radius={[0, 4, 4, 0]}>
                    {perAssetBreakdown.map((row, idx) => (
                      <Cell
                        key={idx}
                        fill={row.pnl >= 0 ? 'hsl(150, 60%, 45%)' : 'hsl(0, 70%, 55%)'}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Equity curve storica con mark-to-market giornaliero (Sprint 6.A.9) */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <TrendingUp className="w-4 h-4" />
            Andamento portafoglio
            <span className="text-xs text-muted-foreground font-normal">
              (valore di mercato giornaliero vs capitale investito)
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {historyLoading ? (
            <div className="text-center py-12 text-muted-foreground text-sm flex items-center justify-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              Calcolo andamento storico…
            </div>
          ) : history.length < 2 ? (
            <div className="text-center py-12 text-muted-foreground text-sm">
              Storico insufficiente. La curva apparirà man mano che il portafoglio matura.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={history} margin={{ left: 0, right: 12, top: 8, bottom: 0 }}>
                <defs>
                  <linearGradient id="mvFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="hsl(150, 60%, 45%)" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="hsl(150, 60%, 45%)" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="cbFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="hsl(220, 70%, 55%)" stopOpacity={0.15} />
                    <stop offset="100%" stopColor="hsl(220, 70%, 55%)" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 10 }}
                  tickFormatter={(d) => {
                    const dt = new Date(d);
                    return `${dt.getDate()}/${dt.getMonth() + 1}`;
                  }}
                  minTickGap={32}
                />
                <YAxis
                  tick={{ fontSize: 11 }}
                  domain={['auto', 'auto']}
                  tickFormatter={(v) => `€${v.toFixed(0)}`}
                  width={72}
                />
                <Tooltip
                  formatter={(v, name) => {
                    const labelMap = {
                      market_value: 'Valore di mercato',
                      cost_basis: 'Capitale investito',
                      pnl: 'PnL',
                    };
                    return [formatCurrency(v), labelMap[name] || name];
                  }}
                  labelFormatter={(l) => `Data: ${l}`}
                />
                <Legend
                  iconType="line"
                  wrapperStyle={{ fontSize: 11 }}
                  formatter={(value) => {
                    const labelMap = {
                      market_value: 'Valore di mercato',
                      cost_basis: 'Capitale investito',
                    };
                    return labelMap[value] || value;
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="cost_basis"
                  stroke="hsl(220, 70%, 55%)"
                  strokeWidth={1.5}
                  strokeDasharray="4 3"
                  fill="url(#cbFill)"
                />
                <Area
                  type="monotone"
                  dataKey="market_value"
                  stroke="hsl(150, 60%, 45%)"
                  strokeWidth={2}
                  fill="url(#mvFill)"
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* Trades table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Trade ({trades.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {trades.length === 0 ? (
            <EmptyState
              icon={History}
              title="Ancora nessun trade"
              description={isReal
                ? 'Inserisci le operazioni del tuo broker per ricostruire il portafoglio.'
                : 'Apri il primo trade manualmente, oppure attiva l\'auto-trader sui portafogli "sintetico live".'}
            />
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Data</TableHead>
                    <TableHead>Simbolo</TableHead>
                    <TableHead className="text-right">Qty</TableHead>
                    <TableHead className="text-right">Entry</TableHead>
                    <TableHead className="text-right">Exit / Now</TableHead>
                    <TableHead className="text-right">Fees</TableHead>
                    <TableHead className="text-right">PnL net</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Azioni</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {trades.map((t) => {
                    const fees = (t.fees_entry || 0) + (t.fees_exit || 0);
                    const entryPrice = t.entry?.price || 0;
                    const exitPrice = t.exit?.price;
                    const currentPrice = t.status === 'open' ? latestPrices[t.symbol] : null;
                    const sideMul = t.side === 'long' ? 1 : -1;
                    // PnL: closed → pnl_abs_net dal DB; open → mark-to-market client
                    let pnl = t.pnl_abs_net;
                    if (pnl == null && currentPrice != null) {
                      pnl = sideMul * t.qty * (currentPrice - entryPrice) - (t.fees_entry || 0);
                    }
                    const priceShown = exitPrice != null ? exitPrice : currentPrice;
                    return (
                      <TableRow key={t.id}>
                        <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                          {formatDate(t.created_at)}
                        </TableCell>
                        <TableCell className="font-medium">{t.symbol}</TableCell>
                        <TableCell className="text-right tabular-nums">{t.qty}</TableCell>
                        <TableCell className="text-right tabular-nums">
                          {formatCurrency(entryPrice)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {priceShown != null ? (
                            <span className={t.status === 'open' ? 'text-muted-foreground' : ''}>
                              {formatCurrency(priceShown)}
                              {t.status === 'open' && <span className="text-[10px] ml-1">live</span>}
                            </span>
                          ) : '—'}
                        </TableCell>
                        <TableCell className="text-right tabular-nums text-xs text-muted-foreground">
                          {fees > 0 ? formatCurrency(fees) : '—'}
                        </TableCell>
                        <TableCell className={`text-right tabular-nums font-medium ${
                          pnl == null ? 'text-muted-foreground' :
                          pnl > 0 ? 'text-emerald-600 dark:text-emerald-400' :
                          pnl < 0 ? 'text-red-600 dark:text-red-400' : ''
                        }`}>
                          {pnl != null ? (pnl > 0 ? '+' : '') + formatCurrency(pnl) : '—'}
                          {t.status === 'open' && pnl != null && (
                            <div className="text-[10px] text-muted-foreground">unrealized</div>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={t.status === 'open' ? 'default' : 'secondary'}
                            className="text-[10px]"
                          >
                            {t.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          {t.status === 'open' ? (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => { setCloseTradeDialog(t); setCPrice(''); }}
                              data-testid={`close-trade-${t.id}`}
                            >
                              Chiudi
                            </Button>
                          ) : null}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Open trade dialog */}
      <Dialog open={openTradeDialog} onOpenChange={setOpenTradeDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Apri trade</DialogTitle>
            <DialogDescription>
              Simula l'apertura di una posizione. Le fees del profilo del portafoglio saranno applicate.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleOpenTrade} className="space-y-3">
            <div>
              <Label>Asset</Label>
              <AssetPicker
                value={oSymbol}
                onChange={setOSymbol}
                placeholder='Cerca per nome o ticker (es. "s&p", "MSCI World")'
                autoFocus
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="o-qty">Quantità</Label>
                <Input
                  id="o-qty" type="number" step="0.0001" min="0.0001"
                  value={oQty} onChange={(e) => setOQty(e.target.value)} required
                />
              </div>
              <div>
                <Label htmlFor="o-price">Prezzo entry (€)</Label>
                <Input
                  id="o-price" type="number" step="0.01" min="0.01"
                  value={oPrice} onChange={(e) => setOPrice(e.target.value)} required
                />
              </div>
            </div>
            <div>
              <Label htmlFor="o-strategy">Strategia (opzionale)</Label>
              <Input
                id="o-strategy" value={oStrategy}
                onChange={(e) => setOStrategy(e.target.value)}
                placeholder={portfolio.strategy_name || 'es. donchian_breakout_20'}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpenTradeDialog(false)} disabled={submitting}>
                Annulla
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? 'Apertura…' : 'Apri'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add asset dialog */}
      <Dialog open={addAssetDialog} onOpenChange={setAddAssetDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Aggiungi asset al portafoglio</DialogTitle>
            <DialogDescription>
              Cerca per nome o ticker. L'asset appare nei suggerimenti per i prossimi trade.
            </DialogDescription>
          </DialogHeader>
          <AssetPicker
            value={newAssetSymbol}
            onChange={setNewAssetSymbol}
            placeholder='Cerca asset (es. "MSCI World", "QQQ")'
            autoFocus
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => { setAddAssetDialog(false); setNewAssetSymbol(''); }}>
              Annulla
            </Button>
            <Button onClick={handleAddAsset} disabled={!newAssetSymbol}>
              Aggiungi {newAssetSymbol}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Past operation dialog */}
      <Dialog open={pastOpDialog} onOpenChange={setPastOpDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              Operazione passata
              {pAddedCount > 0 && (
                <span className="ml-2 text-xs text-muted-foreground font-normal">
                  ({pAddedCount} aggiunte in questa sessione)
                </span>
              )}
            </DialogTitle>
            <DialogDescription>
              Inserisci le operazioni del tuo broker reale. SELL chiude in FIFO il primo BUY con qty coincidente sullo stesso ticker.
            </DialogDescription>
          </DialogHeader>
          <form className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="p-date">Data</Label>
                <Input
                  id="p-date" type="date"
                  value={pDate} onChange={(e) => setPDate(e.target.value)}
                  required
                />
              </div>
              <div>
                <Label htmlFor="p-side">Lato</Label>
                <select
                  id="p-side"
                  value={pSide}
                  onChange={(e) => setPSide(e.target.value)}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="buy">BUY (apre)</option>
                  <option value="sell">SELL (chiude FIFO)</option>
                </select>
              </div>
            </div>
            <div>
              <Label>Asset</Label>
              <AssetPicker
                value={pSymbol}
                onChange={setPSymbol}
                placeholder='Cerca asset del tuo Fineco/broker'
                autoFocus
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="p-qty">Quantità</Label>
                <Input
                  id="p-qty" type="number" step="0.0001" min="0.0001"
                  value={pQty} onChange={(e) => setPQty(e.target.value)} required
                />
              </div>
              <div>
                <Label htmlFor="p-price">Prezzo unitario</Label>
                <Input
                  id="p-price" type="number" step="0.01" min="0.01"
                  value={pPrice} onChange={(e) => setPPrice(e.target.value)} required
                />
                {pPriceLoading && (
                  <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                    <Loader2 className="w-3 h-3 animate-spin" />
                    Recupero prezzo del {pDate}…
                  </p>
                )}
                {!pPriceLoading && pPriceSuggested && !pPriceSuggested.error && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Close yfinance del {pPriceSuggested.date_actual}:{' '}
                    <button
                      type="button"
                      className="font-mono text-primary hover:underline"
                      onClick={() => setPPrice((pPriceSuggested.close_eur ?? pPriceSuggested.close).toFixed(2))}
                    >
                      €{(pPriceSuggested.close_eur ?? pPriceSuggested.close).toFixed(2)}
                    </button>
                    {pPriceSuggested.currency && pPriceSuggested.currency !== 'EUR' && (
                      <span className="text-muted-foreground ml-1">
                        (conv. da {pPriceSuggested.currency} {pPriceSuggested.close.toFixed(2)})
                      </span>
                    )}
                    {pPriceSuggested.date_actual !== pDate && (
                      <span className="text-amber-600 ml-2">
                        ({pDate} non era trading day)
                      </span>
                    )}
                  </p>
                )}
                {!pPriceLoading && pPriceSuggested?.error && (
                  <p className="text-xs text-amber-600 mt-1">
                    Prezzo non trovato per {pSymbol} a {pDate}. Inserisci manualmente.
                  </p>
                )}
              </div>
            </div>
            <DialogFooter className="gap-2 flex-wrap">
              <Button
                type="button" variant="outline"
                onClick={() => setPastOpDialog(false)} disabled={submitting}
              >
                Chiudi
              </Button>
              <Button
                type="button" variant="secondary"
                onClick={(e) => handleAddPastOp(e, true)}
                disabled={submitting}
                data-testid="past-op-add-another"
              >
                Salva e aggiungi un'altra
              </Button>
              <Button
                type="button"
                onClick={(e) => handleAddPastOp(e, false)}
                disabled={submitting}
                data-testid="past-op-submit"
              >
                {submitting ? 'Salvataggio…' : 'Salva e chiudi'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Close trade dialog */}
      <Dialog open={!!closeTradeDialog} onOpenChange={(v) => !v && setCloseTradeDialog(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Chiudi trade</DialogTitle>
            <DialogDescription>
              {closeTradeDialog && (
                <>
                  {closeTradeDialog.symbol} · qty {closeTradeDialog.qty} · entry {formatCurrency(closeTradeDialog.entry?.price || 0)}
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCloseTrade} className="space-y-3">
            <div>
              <Label htmlFor="c-price">Prezzo di uscita (€)</Label>
              <Input
                id="c-price" type="number" step="0.01" min="0.01"
                value={cPrice} onChange={(e) => setCPrice(e.target.value)} required autoFocus
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCloseTradeDialog(null)} disabled={submitting}>
                Annulla
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? 'Chiusura…' : 'Chiudi trade'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
    </TooltipProvider>
  );
};

const KPICard = ({ label, value, subtitle, highlight }) => {
  const valueClass =
    highlight === 'positive' ? 'text-emerald-600 dark:text-emerald-400' :
    highlight === 'negative' ? 'text-red-600 dark:text-red-400' : '';
  const Icon = highlight === 'positive' ? TrendingUp : highlight === 'negative' ? TrendingDown : null;
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="text-xs text-muted-foreground">{label}</div>
        <div className={`text-xl font-bold flex items-center gap-1 mt-1 ${valueClass}`}>
          {Icon && <Icon className="w-4 h-4" />}
          {value}
        </div>
        {subtitle && (
          <div className="text-xs text-muted-foreground mt-1">{subtitle}</div>
        )}
      </CardContent>
    </Card>
  );
};

export default PortfolioDetailPage;
